import { isSameDay } from 'date-fns';
import type { TimeBlock } from '../types';
import { TimeBlock as TimeBlockComponent } from './TimeBlock';
import { TimeBlockPreview } from './TimeBlockPreview';
import { useBlockCreator } from '../hooks/useBlockCreator';

interface DayColumnProps {
  day: Date;
  timeBlocks: TimeBlock[];
  focusedBlockId: string | null;
  editingBlockId: string | null;
  onBlockCreate: (block: { startTime: Date; endTime: Date }) => void;
  onTitleChange: (block: TimeBlock, title: string) => void;
  onTitleBlur: () => void;
  onEditClick: (e: React.MouseEvent, block: TimeBlock) => void;
  onDeleteClick: (block: TimeBlock) => void;
  onUnfocusBlock: () => void;
  onFocusBlock: (blockId: string) => void;
}

export const DayColumn = ({
  day,
  timeBlocks,
  focusedBlockId,
  editingBlockId,
  onBlockCreate,
  onTitleChange,
  onTitleBlur,
  onEditClick,
  onDeleteClick,
  onUnfocusBlock: onDeselectBlock,
  onFocusBlock: focusBlock,
}: DayColumnProps) => {
  const blockCreator = useBlockCreator();
  const dayBlocks = timeBlocks.filter((block) => isSameDay(block.day, day));

  return (
    <div
      className="flex-1 border-r relative h-full"
      onMouseDown={(e) => {
        if (focusedBlockId && !isFocusedBlock(e, focusedBlockId)) {
          onDeselectBlock();
          return;
        }
        if (!(e.target as HTMLElement).closest('.time-block')) {
          blockCreator.actions.onMouseDown();
        }
      }}
      onMouseUp={(e) => {
        if (focusedBlockId && !isFocusedBlock(e, focusedBlockId)) {
          onDeselectBlock();
          return;
        }
        const result = blockCreator.actions.onMouseUp();
        if (result) {
          onBlockCreate(result);
        }
      }}
      onMouseMove={(e) => {
        if (
          !(e.target as HTMLElement).closest('.time-block') &&
          !focusedBlockId
        ) {
          const rect = e.currentTarget.getBoundingClientRect();
          const y = e.clientY - rect.top;
          blockCreator.actions.onCursorMove(y, day);
        } else {
          blockCreator.actions.onMouseLeave();
        }
      }}
      onMouseLeave={blockCreator.actions.onMouseLeave}
    >
      {dayBlocks.map((block) => (
        <TimeBlockComponent
          key={block.id}
          block={block}
          isFocused={block.id === focusedBlockId}
          isInBackground={
            focusedBlockId !== null && block.id !== focusedBlockId
          }
          isEditing={block.id === editingBlockId}
          onTitleChange={onTitleChange}
          onTitleBlur={onTitleBlur}
          onFocusClick={(_, block) => {
            focusBlock(block.id);
          }}
          onEditClick={(e, block) => {
            console.log('editing block', block);
            onEditClick(e, block);
          }}
          onDeleteClick={onDeleteClick}
        />
      ))}
      {blockCreator.state.blockStartMinutes &&
        blockCreator.state.blockEndMinutes && (
          <TimeBlockPreview
            startMinutes={Math.min(
              blockCreator.state.blockStartMinutes,
              blockCreator.state.blockEndMinutes,
            )}
            endMinutes={Math.max(
              blockCreator.state.blockStartMinutes,
              blockCreator.state.blockEndMinutes,
            )}
            className="bg-opacity-30"
          />
        )}
    </div>
  );
};

const isFocusedBlock = (e: React.MouseEvent, blockId: string) => {
  const blockEl = (e.target as HTMLElement).closest(
    `[data-block-id='${blockId}']`,
  );
  return blockEl !== null;
};
